// authMiddleware.ts
import { ApolloLink, HttpLink, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { CognitoUserPool } from 'amazon-cognito-identity-js';

import { manualLogout } from '@/context/CoginitoAuthProvider';

const TENANT_API_KEY = process.env.REACT_APP_TANENT_API_KEY || '';

// Common error handler
export const errorLink = onError(({ networkError, graphQLErrors }) => {
  const unauthorized =
    (networkError as any)?.statusCode === 401 ||
    graphQLErrors?.some(
      (e) =>
        e.extensions?.code === 'UNAUTHENTICATED' ||
        (e.extensions as any)?.statusCode === 401,
    );

  if (unauthorized) {
    manualLogout();
    // localStorage.removeItem('idToken');
    // window.location.href = '/sign-in';
  }
});

// Cognito pool used only to read the current session's id token for request auth.
const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_POOL_ID || '',
  ClientId: process.env.REACT_APP_POOL_CLIENT_ID || '',
});

const getIdToken = (): Promise<string | null> =>
  new Promise((resolve) => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      resolve(null);
      return;
    }
    currentUser.getSession((err: any, session: any) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });

/**
 * Puts the signed-in user's JWT in the Authorization token.
 *
 * AppSync keys its Lambda-authorizer result cache ONLY on the Authorization token, so
 * sending the per-user JWT there gives every user a distinct cache entry. Previously every
 * request carried the shared tenant API key in Authorization and the JWT in the `identity`
 * header (which is not part of the cache key), so within the authorizer's 60s TTL one user's
 * resolver context (ADMIN/CUSTOMER) leaked to everyone else on the tenant — the source of the
 * intermittent "Admin access required" / missing-results errors in this panel.
 *
 * `x-api-key` still carries the tenant key for tenant lookup. `identity` is kept for backward
 * compatibility while the authorizer rolls out. An explicit per-operation `authorization`
 * (e.g. the `'public'` sentinel) is preserved.
 */
export const authLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      getIdToken()
        .then((token) => {
          operation.setContext(
            ({ headers = {} }: { headers?: Record<string, any> }) => {
              const nextHeaders: Record<string, any> = {
                ...headers,
                'x-api-key': headers['x-api-key'] ?? TENANT_API_KEY,
                authorization: headers.authorization ?? token ?? TENANT_API_KEY,
              };
              if (token && !nextHeaders.identity) {
                nextHeaders.identity = token;
              }
              return { headers: nextHeaders };
            },
          );

          const sub = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
          return () => sub.unsubscribe();
        })
        .catch((error) => observer.error(error));
    }),
);

// Separate HTTP links. Auth headers are applied by `authLink`; these just carry the endpoint.
export const AdminHttpLink = new HttpLink({
  uri: process.env.REACT_APP_BASE_RETAIL_APP_URL || '',
});

export const CustomerHttpLink = new HttpLink({
  uri: process.env.REACT_APP_BASE_RETAIL_APP_URL || '',
});
